/**
 * Reports the App's own errors and the Python start-up time to the API, which logs them to
 * Cloud Logging (decided 2026-10-02 instead of Sentry). Never sends learner code, output or the
 * login ID; only the App's error message, the path (no query) and timings. Best effort: a
 * failed report is dropped, and at most a few are sent per page load.
 */
type Kind = 'error' | 'python-failed' | 'python-ready' | 'csp-violation' | 'csp-violation-external';

interface Extra {
  runtime_ms?: number;
  packages_ms?: number;
  total_ms?: number;
}

const MAX_REPORTS = 10;
let sent = 0;
const seen = new Set<string>();

function safePath(): string {
  const path = window.location.pathname;
  return /^\/[A-Za-z0-9/_\-.]*$/.test(path) ? path.slice(0, 200) : '/';
}

export function reportToServer(kind: Kind, message = '', extra: Extra = {}): void {
  const key = `${kind}:${message}`;
  if (sent >= MAX_REPORTS || seen.has(key)) return;
  seen.add(key);
  sent += 1;
  const body: Record<string, unknown> = { kind, message: message.slice(0, 500), page: safePath() };
  for (const [name, value] of Object.entries(extra)) {
    if (typeof value === 'number' && Number.isFinite(value)) {
      body[name] = Math.max(0, Math.min(600_000, Math.round(value)));
    }
  }
  void fetch('/api/client-log', {
    method: 'POST',
    credentials: 'same-origin',
    keepalive: true,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }).catch(() => undefined);
}

/** The message of an error, with the first lines of its stack (App code only). */
export function describeError(error: unknown): string {
  if (error instanceof Error) {
    const stack = (error.stack ?? '')
      .split('\n')
      .slice(1, 4)
      .map((l) => l.trim());
    return [`${error.name}: ${error.message}`, ...stack].join(' | ');
  }
  return typeof error === 'string' ? error : 'unknown error';
}

/** Where a violation happened: the path of an App script, or only the origin of another. */
function violationSource(sourceFile: string, line: number): string {
  try {
    const url = new URL(sourceFile);
    if (url.origin === window.location.origin) return `${url.pathname}:${String(line)}`;
    // Extensions and the like have no web origin ("null").
    return url.origin === 'null' ? '-' : url.origin;
  } catch {
    return '-';
  }
}

/**
 * Trusted Types violations (ADR-0007): HTML or a script URL reached the DOM without going
 * through a policy. Only the directive and where; never the value (`sample`), which may hold
 * text from the page. Other directives are not sent: browser extensions break them too.
 */
export function describeViolation(event: SecurityPolicyViolationEvent): string | null {
  const directive = event.effectiveDirective;
  if (directive !== 'require-trusted-types-for' && directive !== 'trusted-types') return null;
  return `${directive} ${event.disposition} at ${violationSource(event.sourceFile, event.lineNumber)}`;
}

/**
 * Whether the App's own scripts caused a violation (a bug to fix: e-mailed), or something else
 * on the page did, such as a browser extension (recorded only; 2026-10-22, an extension of the
 * operators set off the error e-mail).
 */
export function isOwnScript(event: SecurityPolicyViolationEvent): boolean {
  try {
    return new URL(event.sourceFile).origin === window.location.origin;
  } catch {
    return false;
  }
}

/** Uncaught errors anywhere in the App's own code. */
export function installGlobalErrorReporting(): void {
  document.addEventListener('securitypolicyviolation', (event) => {
    const message = describeViolation(event);
    if (message === null) return;
    reportToServer(isOwnScript(event) ? 'csp-violation' : 'csp-violation-external', message);
  });
  window.addEventListener('error', (event) => {
    reportToServer('error', describeError(event.error ?? event.message));
  });
  window.addEventListener('unhandledrejection', (event) => {
    reportToServer('error', describeError(event.reason));
  });
}
